import { UIIndividualWarningStates } from "../constants/ui-individual-warning-states";
import { Address } from "../../../../../models/address/address";
import { IndividualDto } from "@models/individuals/individual-dto";
import { CountryPipe } from "../../../../../shared/pipes/countryPipe";

export type UIIndividualWarningVisibilityStates = Record<UIIndividualWarningStates, boolean>;

export interface IUIIndividualPropsToReplace {
  [UIIndividualWarningStates.Address]: Address | null;
  [UIIndividualWarningStates.Dob]: string;
  [UIIndividualWarningStates.BirthPlace]: string;
}

function getMostUsedValue(map: Map<string, number>): string {
  const elements = Array.from(map.entries())
    .filter(([value]) => value && value !== '-');

  if (!elements.length) return '';
  const mostUsedElement = elements
    .reduce((mostUsedElement, element) => mostUsedElement[1] >= element[1] ? mostUsedElement : element, elements[0]);

  if (mostUsedElement[1] >= (elements.length / 2)) return mostUsedElement[0];

  return '';
}

export function getUiIndividualWarnings(individual: IndividualDto): {
  warningStateVisible: UIIndividualWarningVisibilityStates,
  individualPropsToUpdate: IUIIndividualPropsToReplace
} {
  const addresses = new Map<string, number>();
  const datesOfBirth = new Map<string, number>();
  const placesOfBirth = new Map<string, number>();

  individual.companyActiveRelationships.forEach(({ individualDataOverride }) => {
    if (!individualDataOverride) return;

    addresses.set(individualDataOverride.address.normalizedFullAddress, (addresses.get(individualDataOverride.address.normalizedFullAddress) ?? 0) + 1);

    if (individualDataOverride.dob) {
      datesOfBirth.set(individualDataOverride.dob.toString(), (addresses.get(individualDataOverride.dob.toString()) ?? 0) + 1);
    }

    const birthPlace = CountryPipe.formatPlaceOfBirth(individualDataOverride.birthCity, individualDataOverride.birthCountry);
    placesOfBirth.set(birthPlace, (addresses.get(birthPlace) ?? 0) + 1);
  });

  const mostUsedAddress = getMostUsedValue(addresses);
  const mostUsedDob = getMostUsedValue(datesOfBirth);
  const mostUsedPlaceOfBirth = getMostUsedValue(placesOfBirth);

  const addressToReplace = individual.relationshipIndividualDtoList.find((relationship) => relationship.individualDataOverride?.address.normalizedFullAddress && relationship.individualDataOverride?.address.normalizedFullAddress == mostUsedAddress)?.individualDataOverride?.address ?? null;
  const visibility = {
    [UIIndividualWarningStates.Address]: Boolean(mostUsedAddress && individual.address.normalizedFullAddress.toLowerCase() !== mostUsedAddress.toLowerCase()),
    [UIIndividualWarningStates.Dob]: Boolean(mostUsedDob && individual.dob?.toString() !== mostUsedDob),
    [UIIndividualWarningStates.BirthPlace]: Boolean(mostUsedPlaceOfBirth && CountryPipe.formatPlaceOfBirth(individual.birthCity, individual.birthCountry).toLowerCase() !== mostUsedPlaceOfBirth.toLowerCase()),
  };
  const valuesToReplace = {
    [UIIndividualWarningStates.Address]: addressToReplace,
    [UIIndividualWarningStates.Dob]: mostUsedDob,
    [UIIndividualWarningStates.BirthPlace]: mostUsedPlaceOfBirth,
  };

  return { warningStateVisible: visibility, individualPropsToUpdate: valuesToReplace };
}
